import { TEXT_PRESETS } from '../../../../../../../../../../components/global/CardDesigner/components/CardDesignerCardElements/CardDesignerCardElementText/helpers';
import {
  getIdentityFullName,
  getIdentityLastName,
  getIdentityName,
} from '../../../../../../../../../../util/identity';
import { getGroupMembersForJerseyColors } from '../../../../../../../../../../util/print-asset-groups';
import { getPrintAssetDisplayImageUrl } from '../../../../../../../../../../util/print-assets';
import {
  SMART_MATE_FIRST_NAME_PLACEHOLDER,
  SMART_MATE_LAST_NAME_PLACEHOLDER,
  getPrintAssetSourceColour,
  getSmartMateTextRecolour,
} from '../../../../../../../../../../util/smart-mate-text-colours';
import {
  CARD_ELEMENT_TYPE,
  CARD_SIDE,
} from '../../../../../../../../../../services/cards/constants';
import { isCardElementEditable } from '../../../../../../../../../../services/cards/components/AccountCard/helpers';

/**
 * Gets PrintProductConfiguration for PrintProduct CustomPrint checkout.
 *
 * @param {Object} card The card.
 * @returns {undefined|{print_product_id, print_product_size_id}}
 */
export const getCardPrintProductConfiguration = card => {
  if (!card) {
    return undefined;
  }

  const cardTemplate =
    card.card_side_groups.records[0].card_sides.records[0].card_template;
  const printProduct = cardTemplate.print_product;
  const sizes = printProduct?.sizes?.records ?? [];
  const printSize = cardTemplate.print_size;
  const printProductSize = printSize
    ? (sizes.find(productSize => productSize.print_size?.id === printSize.id) ??
      sizes?.[0])
    : sizes?.[0];

  if (!printProduct) {
    return undefined;
  }

  return {
    print_product_id: printProduct.id,
    print_product_size_id: printProductSize.id,
  };
};

export const getCardPrintProductSize = card => {
  if (!card) {
    return undefined;
  }

  const cardTemplate =
    card.card_side_groups.records[0].card_sides.records[0].card_template;
  const printProduct = cardTemplate.print_product;
  const sizes = printProduct?.sizes?.records ?? [];
  const printSize = cardTemplate.print_size;
  const printProductSize = printSize
    ? (sizes.find(productSize => productSize.print_size?.id === printSize.id) ??
      sizes?.[0])
    : sizes?.[0];

  if (!printProduct) {
    return undefined;
  }

  return printProductSize;
};

/**
 * Gets Target Account Card required data for a given card
 *
 * @param {Object} card The card.
 * @returns {{ printSize, orientation }}
 */
export const getCardTargetAccountCardConfiguration = card => {
  if (!card) {
    return undefined;
  }

  const cardTemplate =
    card.card_side_groups.records[0].card_sides.records[0].card_template;
  const printSize = cardTemplate.print_size;

  return {
    printSize,
    orientation: cardTemplate?.orientation,
  };
};

/**
 * Given a preset label, determine the contents of the text element
 *
 * @param {Object} data
 * @param {String} data.presetLabel the preset label, off of TEXT_PRESETS
 * @param {Object} data.identity the active identity
 * @param {Object} data.albumMembershipGroup the album membership group
 * @param {String} data.teamName the identity's team name
 * @param {String} data.levelName the identity's level name
 * @param {String} data.gradeName the identity's grade name
 * @param {String} data.departmentName the identity's department name
 * @returns {String}
 */
export const getTextElementPresetData = ({
  presetLabel,
  identity,
  albumMembershipGroup,
  teamName,
  levelName,
  gradeName,
  departmentName,
}) => {
  switch (presetLabel) {
    case TEXT_PRESETS.GALLERY_NAME_INSERT:
      return (
        albumMembershipGroup?.album?.root_album_folder?.name ??
        albumMembershipGroup?.album_folder?.name ??
        ''
      );
    case TEXT_PRESETS.ALBUM_NAME_INSERT:
      return albumMembershipGroup?.album?.name ?? '';
    case TEXT_PRESETS.TEAM_LEVEL_INSERT:
      return levelName ?? '';
    case TEXT_PRESETS.TEAM_NAME_INSERT:
      return teamName ?? '';
    case TEXT_PRESETS.FULL_NAME_INSERT:
      return getIdentityFullName(identity) ?? '';
    case TEXT_PRESETS.FIRST_NAME_INSERT:
      return getIdentityName(identity) ?? '';
    case TEXT_PRESETS.LAST_NAME_INSERT:
      return getIdentityLastName(identity) ?? '';
    case TEXT_PRESETS.YEAR_INSERT:
      return `${new Date().getFullYear()}`;
    case TEXT_PRESETS.GRADE_INSERT:
      return gradeName ?? '';
    case TEXT_PRESETS.ROLE_INSERT:
      return departmentName ?? '';
    default:
      return '';
  }
};

/**
 * The Smart Color Matching group linked to an element: the buyer-side
 * configuration's when it carries the link, else the pro default's — a
 * buyer-created configuration may come back without `print_asset_group`, but
 * the design's smart link lives on the default regardless.
 *
 * @param {?Object} element The card element state.
 * @returns {?Object} The print asset group.
 */
export const getElementPrintAssetGroup = element =>
  element?.cardElementConfiguration?.print_asset_group ??
  element?.defaultCardElementConfiguration?.print_asset_group ??
  null;

/**
 * The print backgrounds a buyer can pick for a Smart-Color-Matching
 * background: the members matching the identity's jersey colors, or the
 * whole group when none match. Asset images swap in the preview URL for the
 * selector UI (the applied configuration uses the real asset image id).
 *
 * @param {?Object} group The print asset group off the background config.
 * @param {Array} jerseyColors The identity's JerseyColorEnum values.
 * @returns {Array} PrintBackgroundSelector-shaped print assets.
 */
export const getSelectableBackgroundPrintAssets = (group, jerseyColors) => {
  if (!group) {
    return [];
  }

  const matching = getGroupMembersForJerseyColors(group, jerseyColors);
  const members =
    matching.length > 0 ? matching : (group.members?.records ?? []);

  return members.map(member => ({
    ...member.print_asset,
    asset_image: {
      ...member.print_asset.asset_image,
      url: getPrintAssetDisplayImageUrl(member.print_asset),
    },
  }));
};

const NAME_PLACEHOLDERS = [
  SMART_MATE_FIRST_NAME_PLACEHOLDER,
  SMART_MATE_LAST_NAME_PLACEHOLDER,
];

/**
 * The text slots a Smart Color Matching background swap may re-colour, with
 * their changes: editable front TEXT elements still showing a template name
 * placeholder (no buyer text yet) whose colour depends on the background's
 * colour. Slots the buyer has typed into are left alone.
 *
 * @param {Object} params The inputs.
 * @param {Array} params.elements Merged element states
 *   (`getAccountCardElementsConfiguration`).
 * @param {?Object} params.group The linked print asset group.
 * @param {?Object} params.printAsset The resolved background print asset.
 * @returns {Array} `{ element, changes }` pairs.
 */
export const getSmartMateTextElementChanges = ({
  elements,
  group,
  printAsset,
}) => {
  const sourceColour = getPrintAssetSourceColour(printAsset);

  if (!sourceColour) {
    return [];
  }

  return (elements ?? []).flatMap(element => {
    const configuration =
      element.cardElementConfiguration ??
      element.defaultCardElementConfiguration ??
      {};
    const placeholder =
      configuration.help_placeholder ??
      element.defaultCardElementConfiguration?.help_placeholder;

    if (
      element.cardElement?.type !== CARD_ELEMENT_TYPE.TEXT ||
      element.side !== CARD_SIDE.FRONT ||
      !isCardElementEditable(element) ||
      !NAME_PLACEHOLDERS.includes(placeholder) ||
      configuration.text
    ) {
      return [];
    }

    const changes = getSmartMateTextRecolour({
      fontColour: configuration.font_color,
      isFirstName: placeholder === SMART_MATE_FIRST_NAME_PLACEHOLDER,
      groupName: group?.name ?? null,
      sourceColour,
    });

    return changes ? [{ element, changes }] : [];
  });
};
